/*
 * pogi-cross-sell — §5.5 PDP "Complete the routine" cross-sell.
 * Ports window.PogiCrossSell behaviour onto Horizon's native cart + variant
 * events. The anchor (page product) is never a buyable tile; complements toggle;
 * any complement already in the cart locks as "In cart ✓" and is excluded from
 * the add + the bundle total. The add posts the hero + picked add-ons in ONE
 * /cart/add.js (include-hero; drops to add-on-only when the hero's already in
 * cart), opens the drawer, and re-renders from the live cart. The discount is a
 * DISPLAY preview only — the real cut is a Shopify automatic discount (§5.5c).
 */
import { StandardEvents } from '@shopify/events';
import { formatShopifyMoney } from '@pogi/money';
import { beginPogiCartUpdate } from '@pogi/cart-contract';
import { addPogiCartLines } from '@pogi/cart-mutation';

const PENDING_ADD_TTL_MS = 5 * 60 * 1000;

const setTranslatedText = (element, value) => {
  if (!element) return;
  if (typeof window.POGI_SET_TRANSLATED_TEXT === 'function') window.POGI_SET_TRANSLATED_TEXT(element, value);
  else element.textContent = value == null ? '' : String(value);
};

class PogiCrossSell extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.discount = (parseFloat(this.dataset.discount) || 0) / 100;
    this.freeShip = parseInt(this.dataset.freeShipCents, 10) || 2500; // cents
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = String(this.dataset.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD');
    this.locale = String(this.dataset.locale || document.documentElement.lang || 'en');
    this.cartUrl = String(this.dataset.cartUrl || '');
    this.cartAddUrl = String(this.dataset.cartAddUrl || '');
    this.sourceId = String(this.dataset.sourceId || '');
    this.heroProductId = String(this.dataset.heroProductId || '');
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.heroVariantId = String(this.dataset.heroVariantId || '');
    this.heroPrice = parseInt(this.dataset.heroPrice, 10) || 0;
    this.heroImg = this.dataset.heroImg || '';
    this.heroAvailable = this.dataset.heroAvailable === 'true';
    this.variantUpdateGeneration = Number.isSafeInteger(this.variantUpdateGeneration)
      ? this.variantUpdateGeneration
      : 0;
    this.variantSelectionBlocked = Boolean(this.variantSelectionBlocked);

    // variant → anchor image map (each format/scent has its own image), so the
    // hero tile + bundle-bar chip can follow Shopify's resolved product selection.
    this.heroImgs = {};
    const imgScript = this.querySelector('[data-xs-hero-imgs]');
    if (imgScript) { try { this.heroImgs = JSON.parse(imgScript.textContent); } catch (e) { /* keep featured */ } }
    this.heroPlans = {};
    const planScript = this.querySelector('[data-xs-hero-plans]');
    if (planScript) { try { this.heroPlans = JSON.parse(planScript.textContent); } catch (e) { /* fail closed below */ } }

    // i18n: JA overrides for the JS-rendered add-button states, from the
    // [data-xs-i18n] blob the section renders via `| t`. EN fallbacks stay inline.
    this.I = {};
    const i18nEl = this.querySelector('[data-xs-i18n]');
    if (i18nEl) { try { this.I = JSON.parse(i18nEl.textContent); } catch (e) { /* keep EN fallbacks */ } }
    // EVERY read falls back to the exact English literal that used to be hardcoded,
    // so a missing key can never change the EN site. [[count]]/[[amount]] placeholders
    // (not {{ }}) so Liquid's t-filter never eats them.
    this.t = (k, fb, vars) => {
      let s = (this.I && this.I[k]) || fb;
      if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
      return s;
    };
    this.anchorImgEl = this.querySelector('[data-xs-anchor-img]');

    this.tiles = Array.from(this.querySelectorAll('[data-xs-tile]'));
    this.picked = this.picked instanceof Set ? this.picked : new Set();
    this.declinedLeads = this.declinedLeads instanceof Set ? this.declinedLeads : new Set();
    this.cartVariantIds = this.cartVariantIds instanceof Set ? this.cartVariantIds : new Set();
    this.cartProductIds = this.cartProductIds instanceof Set ? this.cartProductIds : new Set();
    this.cartReady = false;
    this.addInFlight = Boolean(this.addInFlight);
    this.pendingAdd = this.pendingAdd || this.restorePendingAdd();
    this.pendingResponseSections = this.pendingResponseSections || null;
    this.addOutcomeUnknown = Boolean(this.pendingAdd);
    this.addFailureVisible = Boolean(this.addFailureVisible);
    this.configurationError = Boolean(this.configurationError);
    this.cartSyncGeneration = Number.isSafeInteger(this.cartSyncGeneration) ? this.cartSyncGeneration : 0;

    this.onClick = (e) => {
      if (e.target.closest('[data-xs-retry]')) {
        this.refreshCart({ focusRetryOnError: true, focusReadyOnSuccess: true });
        return;
      }
      if (this.designMode) return;
      if (e.target.closest('[data-xs-add]')) {
        if (this.cartReady && !this.addInFlight && !this.addOutcomeUnknown) this.add();
        return;
      }
      const tile = e.target.closest('[data-xs-tile]');
      if (
        this.cartReady
        && !this.addInFlight
        && !this.variantSelectionBlocked
        && tile
        && !tile.disabled
        && this.visible(tile)
        && !tile.classList.contains('is-owned')
      ) {
        this.addFailureVisible = false;
        const id = tile.dataset.variantId;
        if (this.picked.has(id)) {
          this.picked.delete(id);
          if (tile.dataset.lead === '1') this.declinedLeads.add(id);
        } else {
          this.picked.add(id);
          if (tile.dataset.lead === '1') this.declinedLeads.delete(id);
        }
        this.render();
      }
    };
    this.addEventListener('click', this.onClick);

    // Variant binding — follow Horizon 4.1.3's promise-backed product selection.
    // The event fires before the section response arrives, so only the resolved
    // resource is authoritative for the hero SKU that a cross-sell add will post.
    this.onVariantUpdate = (e) => {
      if (!(e.target instanceof Element) || e.target.closest('product-card')) return;
      const targetProductId = String(e.target.closest('[data-product-id]')?.dataset.productId || '');
      if (targetProductId && targetProductId !== this.heroProductId) return;
      const generation = ++this.variantUpdateGeneration;
      // The native form already represents the shopper's new choice. Until its
      // authoritative response settles, the previous hero must not be buyable.
      // Keep this gate closed after failed, missing or superseded responses.
      this.variantSelectionBlocked = true;
      this.render();
      e.promise?.then(({ detail }) => {
        if (generation !== this.variantUpdateGeneration || !this.isConnected) return;
        const v = detail?.resource;
        const pid = detail?.productId;
        if (String(pid || '') !== this.heroProductId) return;
        this.addFailureVisible = false;
        if (!v) {
          this.heroAvailable = false;
          this.dataset.heroAvailable = 'false';
          this.render();
          return;
        }
        this.heroVariantId = String(v.id);
        if (typeof v.price === 'number') this.heroPrice = v.price;
        this.heroAvailable = v.available === true;
        const title = this.querySelector('[data-xs-hero-title]');
        // Non-breaking glue mirrors the pogi-noorphan Liquid render.
        if (title) title.textContent = v.title ? v.title.replace(/ (?=\S+$)/, ' ') : '';
        // Swap the anchor tile image (and thus the bundle-bar chip) to the selected
        // variant's image so the picture stays in step with the variant-aware text.
        const newImg = this.heroImgs[this.heroVariantId];
        if (newImg) { this.heroImg = newImg; if (this.anchorImgEl) this.anchorImgEl.src = newImg; }
        // Keep the element's live hero state authoritative across same-element
        // disconnect/reconnect cycles (for example, section DOM re-parenting).
        this.dataset.heroVariantId = this.heroVariantId;
        this.dataset.heroPrice = String(this.heroPrice);
        this.dataset.heroImg = this.heroImg;
        this.dataset.heroAvailable = String(this.heroAvailable);
        this.variantSelectionBlocked = false;
        this.render();
      }).catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pogi-cross-sell] product selection failed:', error);
      });
    };
    document.addEventListener(StandardEvents.productSelect, this.onVariantUpdate);

    // Buy Box v2 writes the native selling-plan input synchronously from its
    // mode/frequency controls. Re-render after that delegated click so the
    // routine total and eventual cart line use the same exact allocation.
    this.onPurchaseOptionUpdate = (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (e.type === 'click' && !target?.closest('[data-pogi-mode], [data-pogi-freq]')) return;
      if (e.type === 'change' && !target?.matches('input[name="selling_plan"]')) return;
      window.queueMicrotask(() => {
        if (this.isConnected) this.render();
      });
    };
    document.addEventListener('click', this.onPurchaseOptionUpdate);
    document.addEventListener('change', this.onPurchaseOptionUpdate);
    document.addEventListener('pogi:buybox-ready', this.onPurchaseOptionUpdate);

    // Stay in sync when the cart changes ANYWHERE (e.g. the shopper removes a
    // complement from the drawer): re-sync owned state + re-render so a removed
    // tile unlocks and is clickable again. Skip our own add (already handled).
    this.onCartUpdate = (e) => {
      if (e.detail?.sourceId === this.sourceId || e.detail?.source === this.sourceId) return;
      e.promise?.then(({ detail }) => {
        if (detail?.didError) return;
        return this.refreshCart();
      }).catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pogi-cross-sell] cart update failed:', error);
      });
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);

    // Theme-editor block selection may reveal a variant-gated complement for
    // inspection, but design mode never enables a purchase or tile action.
    this.onBlockSelect = (e) => {
      if (!this.designMode) return;
      const blockId = String((e.detail && e.detail.blockId) || '');
      const eventTile = e.target && typeof e.target.closest === 'function'
        ? e.target.closest('[data-xs-tile]')
        : null;
      const tile = this.tiles.includes(eventTile)
        ? eventTile
        : this.tiles.find((candidate) => String(candidate.dataset.blockId || '') === blockId);
      if (!tile) return;
      this.editorPreviewBlockId = String(tile.dataset.blockId || blockId);
      this.render();
      if (typeof tile.scrollIntoView === 'function') tile.scrollIntoView({ block: 'nearest' });
    };
    this.onBlockDeselect = (e) => {
      if (!this.designMode) return;
      const blockId = String((e.detail && e.detail.blockId) || '');
      if (blockId && blockId !== this.editorPreviewBlockId) return;
      this.editorPreviewBlockId = '';
      this.render();
    };
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    // Paint only server-verifiable visibility/lead state before the cart read.
    // The bar and all controls remain locked until a validated cart response.
    this.render();
    this.refreshCart();
  }

  disconnectedCallback() {
    if (this.onClick) this.removeEventListener('click', this.onClick);
    if (this.onVariantUpdate) document.removeEventListener(StandardEvents.productSelect, this.onVariantUpdate);
    if (this.onPurchaseOptionUpdate) {
      document.removeEventListener('click', this.onPurchaseOptionUpdate);
      document.removeEventListener('change', this.onPurchaseOptionUpdate);
      document.removeEventListener('pogi:buybox-ready', this.onPurchaseOptionUpdate);
    }
    if (this.onCartUpdate) document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    if (this.onBlockSelect) this.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.onBlockDeselect) this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.cartSyncGeneration += 1;
    this.variantUpdateGeneration += 1;
    this.initialized = false;
  }

  async syncCart() {
    if (!this.cartUrl) return null;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(this.cartUrl, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!res.ok) return null;
      const cart = await res.json();
      if (!cart || !Array.isArray(cart.items)) return null;
      const validItems = cart.items.every((item) => (
        item
        && this.variantId(item.variant_id) != null
        && this.variantId(item.product_id) != null
      ));
      if (!validItems) return null;
      return cart;
    } catch (e) {
      return null;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  applyCart(cart) {
    this.cartVariantIds = new Set(cart.items.map((i) => String(i.variant_id)));
    this.cartProductIds = new Set(cart.items.map((i) => String(i.product_id)));
    this.cartLineIdentities = new Set(cart.items.map((item) => this.lineIdentity(
      item.variant_id,
      item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null,
    )));
    this.cart = cart;
  }

  setSyncState(state) {
    const ready = state === 'ready';
    const loading = state === 'loading';
    const uncertain = state === 'uncertain';
    this.cartReady = ready;
    this.dataset.xsReady = String(ready);
    this.dataset.xsState = state;
    this.setAttribute('aria-busy', String(loading));

    const status = this.querySelector('[data-xs-loading]');
    const spinner = this.querySelector('[data-xs-spinner]');
    const loadingLabel = this.querySelector('[data-xs-loading-label]');
    const errorLabel = this.querySelector('[data-xs-error-label]');
    const retry = this.querySelector('[data-xs-retry]');
    const barMain = this.querySelector('[data-xs-bar-main]');
    const addButton = this.querySelector('[data-xs-add]');

    if (status) status.hidden = ready;
    if (spinner) spinner.hidden = !loading;
    if (loadingLabel) loadingLabel.hidden = !loading;
    if (errorLabel) {
      if (uncertain) {
        setTranslatedText(
          errorLabel,
          this.t('cart_uncertain', 'Cart status changed. Opening your cart…'),
        );
      } else if (state === 'error') {
        setTranslatedText(
          errorLabel,
          this.t('cart_error', 'Unable to refresh your cart. Try again.'),
        );
      }
      errorLabel.hidden = state !== 'error' && !uncertain;
    }
    if (retry) retry.hidden = state !== 'error';
    if (barMain) barMain.hidden = !ready;
    if (addButton) {
      addButton.hidden = !ready;
      if (!ready) addButton.disabled = true;
    }
    if (!ready) this.tiles.forEach((tile) => { tile.disabled = true; });
    if (loading) this.announce('loading', 'Loading');
    else if (state === 'error') this.announce('cart_error', 'Unable to refresh your cart. Try again.');
    else if (uncertain) this.announce('cart_uncertain', 'Cart status changed. Opening your cart…');
    else this.clearAnnouncement();
  }

  announce(key, fallback) {
    const liveStatus = this.querySelector('[data-xs-live-status]');
    if (!liveStatus) return;
    const message = this.t ? this.t(key, fallback) : fallback;
    const generation = (this.liveAnnouncementGeneration || 0) + 1;
    this.liveAnnouncementGeneration = generation;
    setTranslatedText(liveStatus, '');
    window.setTimeout(() => {
      if (generation === this.liveAnnouncementGeneration) setTranslatedText(liveStatus, message);
    }, 0);
  }

  clearAnnouncement() {
    const liveStatus = this.querySelector('[data-xs-live-status]');
    this.liveAnnouncementGeneration = (this.liveAnnouncementGeneration || 0) + 1;
    if (liveStatus) setTranslatedText(liveStatus, '');
  }

  async refreshCart({ focusRetryOnError = false, focusReadyOnSuccess = false } = {}) {
    const generation = ++this.cartSyncGeneration;
    this.setSyncState('loading');
    const cart = await this.syncCart();
    if (generation !== this.cartSyncGeneration) return false;
    if (!cart) {
      this.setSyncState('error');
      if (focusRetryOnError) {
        const retry = this.querySelector('[data-xs-retry]');
        if (retry && typeof retry.focus === 'function') retry.focus();
      }
      return false;
    }
    this.applyCart(cart);

    if (this.pendingAdd) {
      const storageSync = this.reconcilePendingFromStorage(this.pendingAdd);
      this.pendingAdd = storageSync.pending;

      // Another instance already resolved this exact product-scoped transaction.
      // Apply this validated cart snapshot, converge locally, and never emit the
      // owner's success event or open the drawer a second time.
      if (storageSync.resolved) {
        this.cartUpdate?.resolve(cart, { resolution: storageSync.resolved.resolution });
        this.cartUpdate = null;
        this.addOutcomeUnknown = false;
        this.addFailureVisible = ['failed', 'expired'].includes(storageSync.resolved.resolution);
        this.setSyncState('ready');
        this.render();
        if (this.addFailureVisible) {
          const errorKey = storageSync.resolved.resolution === 'failed' ? 'add_error' : 'cart_error';
          const fallback = storageSync.resolved.resolution === 'failed'
            ? 'Error adding to cart'
            : 'Unable to refresh your cart. Try again.';
          this.announce(errorKey, fallback);
        }
        if (focusReadyOnSuccess || (focusRetryOnError && this.addFailureVisible)) this.focusReadyAction();
        return true;
      }

      const pending = this.pendingAdd;
      if (!pending) {
        this.cartUpdate?.resolve(cart, { resolution: 'already-resolved' });
        this.cartUpdate = null;
        this.addOutcomeUnknown = false;
        this.setSyncState('ready');
        this.render();
        if (focusReadyOnSuccess) this.focusReadyAction();
        return true;
      }
      const allIntendedPresent = pending.variantIds.every((id, index) => this.cartLineIdentities.has(
        this.lineIdentity(id, pending.sellingPlanIds[index]),
      ));

      if (allIntendedPresent) {
        const responseSections = this.takeResponseSections(pending.transactionId);
        const currentCartToken = this.cartToken(cart);
        const cartChanged = Boolean(
          pending.cartToken
          && currentCartToken
          && pending.cartToken !== currentCartToken
        );
        // Exact items in a different-token cart are authoritative cart truth, but
        // do not prove this transaction put them there. Resolve without a second
        // drawer/update notification; the current snapshot already owns them.
        const resolution = cartChanged ? 'cart-changed-present' : 'confirmed';
        const shouldNotify = this.resolveStoredPending(pending, resolution);
        this.pendingAdd = null;
        this.addOutcomeUnknown = false;
        this.addFailureVisible = false;
        this.setSyncState('ready');
        this.render();
        if (shouldNotify) {
          if (!this.notifyCartSuccess(responseSections)) this.focusSectionHeading();
        } else {
          this.cartUpdate?.resolve(cart, { resolution });
          this.cartUpdate = null;
        }
        return true;
      }

      // This bound is far longer than the 12-second client timeout and a normal
      // Shopify cart mutation. A validated snapshot still lacking the items
      // after five minutes releases the stale lock for a deliberate add.
      if (this.pendingExpired(pending)) {
        this.cartUpdate?.fail(new Error('Cart update confirmation expired'), cart, { resolution: 'expired' });
        this.cartUpdate = null;
        this.takeResponseSections(pending.transactionId);
        this.resolveStoredPending(pending, 'expired');
        this.pendingAdd = null;
        this.addOutcomeUnknown = false;
        this.addFailureVisible = true;
        this.setSyncState('ready');
        this.render();
        this.announce('cart_error', 'Unable to refresh your cart. Try again.');
        if (focusRetryOnError || focusReadyOnSuccess) this.focusReadyAction();
        return true;
      }

      // Never resolve against a cart snapshot taken while the POST is still in
      // flight. It may commit after this GET and make an apparent absence stale.
      if (pending.postStatus === 'posting') {
        return this.lockPendingAdd(focusRetryOnError);
      }

      // A definite non-2xx POST plus a validated cart snapshot is a safe,
      // reconciled failure. A deliberate shopper click may retry the POST.
      if (pending.postStatus === 'failed') {
        this.cartUpdate?.fail(new Error('Error adding to cart'), cart, { resolution: 'failed' });
        this.cartUpdate = null;
        this.takeResponseSections(pending.transactionId);
        this.resolveStoredPending(pending, 'failed');
        this.pendingAdd = null;
        this.addOutcomeUnknown = false;
        this.addFailureVisible = true;
        this.setSyncState('ready');
        this.render();
        this.announce('add_error', 'Error adding to cart');
        if (focusRetryOnError || focusReadyOnSuccess) this.focusReadyAction();
        return true;
      }

      // A 2xx or transport-uncertain POST can commit after an earlier GET.
      // Retain its exact intent and permit GET-only retries until confirmed.
      return this.lockPendingAdd(focusRetryOnError);
    }

    this.addOutcomeUnknown = false;
    this.setSyncState('ready');
    this.render();
    if (focusReadyOnSuccess) this.focusReadyAction();
    return true;
  }

  lockPendingAdd(focusRetryOnError) {
    this.addOutcomeUnknown = true;
    const uncertain = this.pendingAdd?.postStatus === 'unknown';
    // Paint the validated partial/absent cart truth, then synchronously lock the
    // action shell. Both operations happen in one task, so no enabled frame is
    // exposed while owned/remaining card state stays accurate.
    this.setSyncState('ready');
    this.render();
    this.setSyncState(uncertain ? 'uncertain' : 'error');
    if (focusRetryOnError && !uncertain) {
      const retry = this.querySelector('[data-xs-retry]');
      if (retry && typeof retry.focus === 'function') retry.focus();
    }
    return false;
  }

  focusReadyAction() {
    const addButton = this.querySelector('[data-xs-add]');
    if (addButton && !addButton.hidden && !addButton.disabled && typeof addButton.focus === 'function') {
      addButton.focus();
      return;
    }
    const barMain = this.querySelector('[data-xs-bar-main]');
    if (barMain && !barMain.hidden && typeof barMain.focus === 'function') barMain.focus();
  }

  focusSectionHeading() {
    const title = this.querySelector('[data-xs-title]');
    if (title && typeof title.focus === 'function') title.focus();
  }

  takeResponseSections(transactionId) {
    const response = this.pendingResponseSections;
    if (!response || response.transactionId !== transactionId) return undefined;
    this.pendingResponseSections = null;
    return response.sections;
  }

  notifyCartSuccess(sections) {
    const drawer = document.querySelector('theme-drawer#cart-drawer cart-drawer-component');
    const drawerWillOpen = Boolean(drawer && drawer.hasAttribute('auto-open'));
    try {
      this.cartUpdate?.resolve(this.cart || {}, {
        sections,
      });
      this.cartUpdate = null;
    } catch (e) { /* cart is already verified; do not reopen the transaction */ }
    return drawerWillOpen;
  }

  pendingStorageKey() {
    const productId = this.variantId(this.heroProductId);
    return productId == null ? '' : `pogi-cross-sell:pending-add:v2:${productId}`;
  }

  pendingMemoryStore() {
    try {
      if (!(window.__POGI_CROSS_SELL_PENDING instanceof Map)) {
        window.__POGI_CROSS_SELL_PENDING = new Map();
      }
      return window.__POGI_CROSS_SELL_PENDING;
    } catch (e) {
      return null;
    }
  }

  readMemoryPending() {
    const key = this.pendingStorageKey();
    const store = this.pendingMemoryStore();
    if (!key || !store) return null;
    const normalized = this.normalizePendingRecord(store.get(key));
    if (!normalized) {
      try { store.delete(key); } catch (e) { /* retain the element-local lock */ }
    }
    return normalized;
  }

  writeMemoryPending(pending, { allowReplace = false } = {}) {
    const key = this.pendingStorageKey();
    const store = this.pendingMemoryStore();
    const normalized = this.normalizePendingRecord(pending);
    if (!key || !store || !normalized) return false;
    const current = this.readMemoryPending();
    if (current && current.transactionId !== normalized.transactionId && !allowReplace) return false;
    try {
      store.set(key, {
        ...normalized,
        variantIds: [...normalized.variantIds],
        sellingPlanIds: [...normalized.sellingPlanIds],
      });
      const confirmed = this.readMemoryPending();
      return Boolean(confirmed && confirmed.transactionId === normalized.transactionId);
    } catch (e) {
      return false;
    }
  }

  cartToken(cart = this.cart) {
    const token = String((cart && cart.token) || '').trim();
    return token.length <= 512 ? token : '';
  }

  pendingExpired(pending, now = Date.now()) {
    const createdAt = Number(pending && pending.createdAt);
    return !Number.isFinite(createdAt)
      || createdAt <= 0
      // A backward wall-clock step must retain an ambiguous transaction. Once
      // the clock catches up, the normal five-minute bound applies.
      || (now >= createdAt && now - createdAt >= PENDING_ADD_TTL_MS);
  }

  pendingRecordTime(pending) {
    if (!pending) return 0;
    const createdAt = Number(pending.createdAt) || 0;
    const resolvedAt = pending.resolved ? Number(pending.resolvedAt) || 0 : 0;
    return Math.max(createdAt, resolvedAt);
  }

  mergeSameTransaction(records) {
    const candidates = records.filter(Boolean);
    if (!candidates.length) return null;
    const transactionId = candidates[0].transactionId;
    const same = candidates.filter((record) => record.transactionId === transactionId);
    const statusRank = { posting: 0, unknown: 1, success: 2, failed: 2 };
    return same.reduce((latest, candidate) => {
      if (!latest) return candidate;
      if (candidate.resolved !== latest.resolved) return candidate.resolved ? candidate : latest;
      if (candidate.resolved && candidate.resolvedAt > latest.resolvedAt) return candidate;
      if (!candidate.resolved && statusRank[candidate.postStatus] > statusRank[latest.postStatus]) return candidate;
      return latest;
    }, null);
  }

  newTransactionId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) { /* use the collision-resistant fallback below */ }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  }

  normalizePendingRecord(value) {
    if (!value || typeof value !== 'object') return null;
    const allowedStatuses = new Set(['posting', 'success', 'failed', 'unknown']);
    const allowedResolutions = new Set([
      'confirmed',
      'failed',
      'expired',
      'uncertain',
      'cart-changed-present',
      'superseded',
    ]);
    const transactionId = String(value.transactionId || '');
    const productId = String(value.productId || '');
    const cartToken = String(value.cartToken || '');
    const variantIds = Array.isArray(value.variantIds) ? value.variantIds.map(String) : [];
    const sellingPlanIds = Array.isArray(value.sellingPlanIds)
      ? value.sellingPlanIds.map((id) => id == null || id === '' ? null : String(id))
      : variantIds.map(() => null);
    const createdAt = Number(value.createdAt);
    const resolved = value.resolved === true;
    const resolution = String(value.resolution || '');
    const validIds = variantIds.length > 0
      && variantIds.every((id) => this.variantId(id) != null)
      && new Set(variantIds).size === variantIds.length;
    const validPlans = sellingPlanIds.length === variantIds.length
      && sellingPlanIds.every((id) => id == null || this.variantId(id) != null);
    const validTransaction = /^[A-Za-z0-9._:-]{8,160}$/.test(transactionId);
    const validProduct = this.variantId(productId) != null && productId === String(this.heroProductId);
    const resolvedAt = Number(value.resolvedAt) || 0;
    const validTime = Number.isSafeInteger(createdAt) && createdAt > 0;
    const validResolution = !resolved || allowedResolutions.has(resolution);
    const validResolvedAt = !resolved || (Number.isSafeInteger(resolvedAt) && resolvedAt > 0);
    if (
      !validIds
      || !validPlans
      || !validTransaction
      || !validProduct
      || !validTime
      || cartToken.length > 512
      || !allowedStatuses.has(value.postStatus)
      || !validResolution
      || !validResolvedAt
    ) return null;
    // Earlier parity builds persisted an ambiguous transport outcome as a
    // resolved `uncertain` tombstone. It is not a safe resolution: Shopify may
    // still have committed that POST. Quarantine any young legacy record as the
    // same unresolved unknown lock until exact cart reconciliation or expiry.
    const quarantinedUncertain = resolved && resolution === 'uncertain';
    return {
      transactionId,
      productId,
      cartToken,
      variantIds,
      sellingPlanIds,
      postStatus: quarantinedUncertain ? 'unknown' : value.postStatus,
      createdAt,
      resolved: quarantinedUncertain ? false : resolved,
      resolution: quarantinedUncertain ? '' : resolution,
      resolvedAt: quarantinedUncertain ? 0 : resolvedAt,
    };
  }

  removeInvalidPendingRecord(key) {
    try {
      if (key && window.sessionStorage) window.sessionStorage.removeItem(key);
    } catch (e) { /* an unavailable store must not weaken an in-memory lock */ }
  }

  readStoredPendingState() {
    const key = this.pendingStorageKey();
    if (!key) return { available: false, record: null };
    let raw;
    try {
      if (!window.sessionStorage) return { available: false, record: null };
      raw = window.sessionStorage.getItem(key);
    } catch (e) {
      return { available: false, record: null };
    }
    if (!raw) return { available: true, record: null };
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      this.removeInvalidPendingRecord(key);
      return { available: true, record: null };
    }
    const normalized = this.normalizePendingRecord(parsed);
    if (!normalized) this.removeInvalidPendingRecord(key);
    return { available: true, record: normalized };
  }

  readStoredPending() {
    return this.readStoredPendingState().record;
  }

  reconcilePendingFromStorage(localPending) {
    if (!localPending) return { pending: null, resolved: null };
    const memory = this.readMemoryPending();
    const stored = this.readStoredPending();

    // The window-level product claim is the same-page authority. Every
    // production add writes it synchronously before the POST, so a different
    // stored tombstone cannot erase a newer unresolved in-memory transaction
    // when sessionStorage later becomes read-only or fails verification.
    if (memory) {
      if (memory.transactionId !== localPending.transactionId) {
        if (memory.resolved) return { pending: null, resolved: memory };
        return { pending: memory, resolved: null };
      }
      const latest = this.mergeSameTransaction([
        localPending,
        memory,
        stored && stored.transactionId === localPending.transactionId ? stored : null,
      ]);
      this.writeMemoryPending(latest, { allowReplace: true });
      if (latest.resolved) return { pending: null, resolved: latest };
      return { pending: latest, resolved: null };
    }

    if (stored && stored.transactionId === localPending.transactionId) {
      const latest = this.mergeSameTransaction([localPending, stored]);
      this.writeMemoryPending(latest, { allowReplace: true });
      if (latest.resolved) return { pending: null, resolved: latest };
      return { pending: latest, resolved: null };
    }

    // Without a same-page claim, a young unresolved stored transaction remains
    // an active lock. A different resolved tombstone may supersede only when it
    // is demonstrably newer; older or equal records retain the local lock.
    if (stored && !stored.resolved && !this.pendingExpired(stored)) {
      this.writeMemoryPending(stored, { allowReplace: true });
      return { pending: stored, resolved: null };
    }
    if (
      stored
      && stored.resolved
      && this.pendingRecordTime(stored) > this.pendingRecordTime(localPending)
    ) {
      this.writeMemoryPending(stored, { allowReplace: true });
      return { pending: null, resolved: stored };
    }

    this.writeMemoryPending(localPending, { allowReplace: true });
    return { pending: localPending, resolved: null };
  }

  writePendingRecord(pending, { allowReplace = false } = {}) {
    const key = this.pendingStorageKey();
    if (!key) return false;
    try {
      if (!window.sessionStorage) return false;
      const current = this.readStoredPending();
      if (
        current
        && current.transactionId !== pending.transactionId
        && !allowReplace
      ) return false;
      window.sessionStorage.setItem(key, JSON.stringify(pending));
      const confirmed = this.readStoredPending();
      return Boolean(confirmed && confirmed.transactionId === pending.transactionId);
    } catch (e) {
      return false;
    }
  }

  claimPendingAdd(transaction) {
    const memory = this.readMemoryPending();
    const stored = this.readStoredPending();
    const active = [memory, stored]
      .filter((current) => (
        current
        && current.transactionId !== transaction.transactionId
        && !current.resolved
        && !this.pendingExpired(current)
      ))
      .sort((a, b) => this.pendingRecordTime(b) - this.pendingRecordTime(a))[0];
    if (active) {
      this.pendingAdd = active;
      this.addOutcomeUnknown = true;
      this.writeMemoryPending(active, { allowReplace: true });
      return false;
    }

    // Claim synchronously in window memory before touching fallible browser
    // storage. This prevents duplicate same-product POSTs from sibling sections
    // even when sessionStorage is missing, read-only, or throws.
    this.writeMemoryPending(transaction, { allowReplace: true });
    this.writePendingRecord(transaction, { allowReplace: true });

    // A competing unresolved storage record discovered after a failed write
    // still blocks before the POST begins. Older resolved tombstones do not.
    const afterWrite = this.readStoredPending();
    if (
      afterWrite
      && afterWrite.transactionId !== transaction.transactionId
      && !afterWrite.resolved
      && !this.pendingExpired(afterWrite)
    ) {
      this.pendingAdd = afterWrite;
      this.addOutcomeUnknown = true;
      this.writeMemoryPending(afterWrite, { allowReplace: true });
      return false;
    }
    return true;
  }

  persistPendingAdd(pending = this.pendingAdd) {
    if (!pending) return false;
    const memoryWritten = this.writeMemoryPending(pending, { allowReplace: true });
    const stored = this.readStoredPending();
    if (
      stored
      && stored.transactionId !== pending.transactionId
      && !stored.resolved
      && !this.pendingExpired(stored)
    ) return memoryWritten;
    const canReplaceStored = !stored
      || stored.transactionId === pending.transactionId
      || this.pendingExpired(stored)
      || (
        stored.resolved
        && this.pendingRecordTime(stored) <= this.pendingRecordTime(pending)
      );
    if (!canReplaceStored) return memoryWritten;

    const storedWritten = this.writePendingRecord(pending, {
      allowReplace: Boolean(stored && stored.transactionId !== pending.transactionId),
    });
    return memoryWritten || storedWritten;
  }

  claimResolution(transactionId) {
    try {
      if (!(window.__POGI_CROSS_SELL_RESOLVED instanceof Set)) {
        window.__POGI_CROSS_SELL_RESOLVED = new Set();
      }
      if (window.__POGI_CROSS_SELL_RESOLVED.has(transactionId)) return false;
      window.__POGI_CROSS_SELL_RESOLVED.add(transactionId);
      return true;
    } catch (e) {
      if (this.lastResolvedTransactionId === transactionId) return false;
      this.lastResolvedTransactionId = transactionId;
      return true;
    }
  }

  resolveStoredPending(pending, resolution) {
    const sync = this.reconcilePendingFromStorage(pending);
    if (sync.resolved) return false;
    if (!sync.pending || sync.pending.transactionId !== pending.transactionId) return false;
    const resolved = {
      ...sync.pending,
      resolved: true,
      resolution,
      resolvedAt: Date.now(),
    };
    this.writeMemoryPending(resolved, { allowReplace: true });
    const current = this.readStoredPending();
    const canReplaceStored = !current
      || current.transactionId === resolved.transactionId
      || (
        current.resolved
        && this.pendingRecordTime(current) <= this.pendingRecordTime(resolved)
      )
      || this.pendingExpired(current);
    if (canReplaceStored) {
      this.writePendingRecord(resolved, {
        allowReplace: Boolean(current && current.transactionId !== resolved.transactionId),
      });
    }
    const firstResolution = this.claimResolution(pending.transactionId);
    return resolution === 'confirmed' && firstResolution;
  }

  restorePendingAdd() {
    const memory = this.readMemoryPending();
    const stored = this.readStoredPending();
    let pending = memory && !memory.resolved
      ? memory
      : stored && !stored.resolved
        ? stored
        : null;
    if (!pending) return null;
    const sync = this.reconcilePendingFromStorage(pending);
    if (sync.resolved || !sync.pending) return null;
    pending = sync.pending;
    // A reload cannot prove whether a request marked "posting" committed.
    if (pending.postStatus === 'posting') {
      pending.postStatus = 'unknown';
      this.persistPendingAdd(pending);
    }
    return pending;
  }

  productForm() {
    return Array.from(document.querySelectorAll('product-form-component[data-product-id]')).find((component) => (
      String(component.dataset.productId || '') === this.heroProductId
      && !component.closest('product-card')
    )) || null;
  }

  heroPurchase() {
    const rawPlanId = String(this.productForm()?.querySelector('input[name="selling_plan"]')?.value || '').trim();
    if (!rawPlanId) return { valid: true, sellingPlanId: null, price: this.heroPrice };
    const planId = this.variantId(rawPlanId);
    const planPrice = Number(this.heroPlans?.[this.heroVariantId]?.[String(planId)]);
    if (planId == null || !Number.isSafeInteger(planPrice) || planPrice < 0) {
      return { valid: false, sellingPlanId: null, price: this.heroPrice };
    }
    return { valid: true, sellingPlanId: String(planId), price: planPrice };
  }

  lineIdentity(variantId, sellingPlanId = null) {
    return `${String(variantId || '')}:${sellingPlanId == null ? '' : String(sellingPlanId)}`;
  }

  heroInCart(purchase = this.heroPurchase()) {
    return this.cartLineIdentities.has(this.lineIdentity(this.heroVariantId, purchase.sellingPlanId));
  }
  owned(t) {
    // variant-only complements (an ALT format of the page's OWN product, e.g.
    // Refill Rolls offered on a Tie-Handle bag) must match the exact variant —
    // matching the product would falsely lock the tile whenever ANY variant of
    // that product is already in the cart (the anchor always is, post-add).
    if (t.dataset.variantOnly === '1') return this.cartVariantIds.has(t.dataset.variantId);
    return this.cartVariantIds.has(t.dataset.variantId) || this.cartProductIds.has(t.dataset.productId);
  }
  // A complement gated to HIDE for specific hero variants (data-hide-for list).
  hiddenForHero(t) {
    const list = t.dataset.hideFor;
    if (!list) return false;
    return list.split(',').map((s) => s.trim()).filter(Boolean).includes(String(this.heroVariantId));
  }
  // A complement gated to SHOW only for an allow-list of hero variants
  // (data-show-for) — e.g. the scented dispenser shows ONLY on scented refill
  // rolls; the alt bag format shows ONLY on Jumbo / Tie-Handle. Empty = always.
  shownForHero(t) {
    const list = t.dataset.showFor;
    if (!list) return true;
    return list.split(',').map((s) => s.trim()).filter(Boolean).includes(String(this.heroVariantId));
  }
  // Net visibility for the current hero variant.
  visible(t) { return !this.hiddenForHero(t) && this.shownForHero(t); }

  money(cents) {
    return formatShopifyMoney(Number(cents) || 0, this.moneyFormat, this.currency, this.locale);
  }

  setText(sel, val) { setTranslatedText(this.querySelector(sel), val); }

  render() {
    const ready = this.cartReady === true;
    const heroPurchase = this.heroPurchase();
    const heroInCart = ready && this.heroInCart(heroPurchase);
    const toAdd = [];
    const ownedAddons = [];

    // Only a currently visible lead is preselected. A newly revealed lead is
    // armed unless the shopper explicitly declined that exact lead earlier.
    this.tiles.forEach((tile) => {
      const id = tile.dataset.variantId;
      if (!this.visible(tile)) this.picked.delete(id);
      else if (tile.dataset.lead === '1' && !this.declinedLeads.has(id)) this.picked.add(id);
    });

    this.tiles.forEach((t) => {
      // Variant-gated complement: hide when the page's selected variant already
      // includes it (the 50-roll +2 dispensers bundle hides the Dispenser tile).
      const eligible = this.visible(t);
      const previewed = this.designMode
        && this.editorPreviewBlockId
        && String(t.dataset.blockId || '') === this.editorPreviewBlockId;
      t.hidden = !eligible && !previewed;
      if (!eligible) {
        this.picked.delete(t.dataset.variantId);
        t.classList.remove('is-owned', 'is-on');
        t.setAttribute('aria-pressed', 'false');
        t.disabled = true;
        this.setTileState(t, '');
        return;
      }

      // Before a verified cart read, paint only variant visibility and the
      // configured lead selection. Never infer an empty cart or enable controls.
      if (!ready) {
        const on = this.picked.has(t.dataset.variantId);
        t.classList.remove('is-owned');
        t.classList.toggle('is-on', on);
        t.setAttribute('aria-pressed', String(on));
        t.disabled = true;
        this.setTileState(t, on ? this.t('tile_in_kit', 'In your kit') : this.t('tile_add', 'Add'));
        return;
      }

      const own = this.owned(t);
      // Once owned, drop it from `picked` so deleting it from the cart returns
      // the tile to UNCHECKED (Add) — not re-armed as a selected add-on.
      if (own) {
        this.picked.delete(t.dataset.variantId);
        if (t.dataset.lead === '1') this.declinedLeads.add(t.dataset.variantId);
      }
      const on = this.picked.has(t.dataset.variantId) && !own;
      t.classList.toggle('is-owned', own);
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-pressed', String(on || own));
      t.disabled = this.designMode || own || this.variantSelectionBlocked;
      this.setTileState(t, own ? this.t('tile_in_cart', 'In cart ✓') : on ? this.t('tile_in_kit', 'In your kit') : this.t('tile_add', 'Add'));
      if (own) ownedAddons.push(t); else if (on) toAdd.push(t);
    });

    const heroPrice = this.querySelector('[data-xs-hero-price]');
    if (heroPrice) setTranslatedText(heroPrice, this.money(heroPurchase.price));

    if (!ready) return;

    const heroChip = this.querySelector('[data-xs-hero-chip]');
    setTranslatedText(heroChip, heroInCart ? this.t('hero_in_cart', 'In your cart') : this.t('hero_this_item', 'This item'));
    // Checkmark only in the in-cart state; "This item" (no ✓) when not in cart.
    const heroCheck = this.querySelector('[data-xs-hero-check]');
    if (heroCheck) heroCheck.hidden = !heroInCart;

    const addonPrices = toAdd.map((t) => parseInt(t.dataset.price, 10) || 0);
    const addonsList = addonPrices.reduce((sum, price) => sum + price, 0);
    const addonSave = addonPrices.reduce((sum, price) => sum + Math.floor(price * this.discount), 0);
    const addonsKit = addonsList - addonSave;
    const heroAdd = !heroInCart;
    const addCount = (heroAdd ? 1 : 0) + toAdd.length;
    const heroSave = heroAdd ? Math.max(0, this.heroPrice - heroPurchase.price) : 0;
    const save = heroSave + addonSave;
    const displayTotal = (heroAdd ? heroPurchase.price : 0) + addonsKit;
    const displayStruck = (heroAdd ? this.heroPrice : 0) + addonsList;
    const everythingInCart = heroInCart && this.tiles.filter((t) => this.visible(t)).every((t) => this.owned(t));
    const pct = Math.round(this.discount * 100);

    // bar label
    let barLabel;
    if (everythingInCart) barLabel = this.t('bar_routine', 'Your routine');
    else if (heroInCart) barLabel = toAdd.length === 0 ? this.t('bar_addons', 'Your add-ons') : this.t('bar_addons_count', 'Add-ons · ' + toAdd.length + ' item' + (toAdd.length > 1 ? 's' : ''), { count: toAdd.length });
    else barLabel = this.t('bar_kit_count', 'Your kit · ' + addCount + ' item' + (addCount > 1 ? 's' : ''), { count: addCount });
    this.setText('[data-xs-barlabel]', barLabel);

    // prices
    const hint = this.querySelector('[data-xs-price-hint]');
    const total = this.querySelector('[data-xs-price-total]');
    const struck = this.querySelector('[data-xs-price-struck]');
    const saveLabel = this.querySelector('[data-xs-price-save]');
    if (hint) {
      hint.hidden = addCount !== 0;
      setTranslatedText(
        hint,
        everythingInCart
          ? this.t('hint_all_in_cart', "Your whole routine’s in the cart ✓")
          : this.t('hint_add_essentials', 'Add the essentials to complete your routine.')
      );
    }
    if (total) {
      total.hidden = addCount === 0;
      setTranslatedText(total, this.money(displayTotal));
    }
    if (struck) {
      struck.hidden = addCount === 0 || save <= 0;
      setTranslatedText(struck, this.money(displayStruck));
    }
    if (saveLabel) {
      saveLabel.hidden = addCount === 0 || save <= 0;
      setTranslatedText(saveLabel, this.t('save_caps', 'SAVE ' + this.money(save), { amount: this.money(save) }));
    }

    // chips: hero + selected/owned add-ons
    const chips = this.querySelector('[data-xs-chips]');
    if (chips) {
      const imgs = [this.heroImg].concat(toAdd.map((t) => t.dataset.img), ownedAddons.map((t) => t.dataset.img)).filter(Boolean);
      chips.innerHTML = imgs.map((src, i) => (i ? '<span class="x-plus">+</span>' : '') + '<span class="x-chip"><img src="' + src + '" alt=""></span>').join('');
    }

    // add button — ONE cart evaluation drives label + disabled, in step with the
    // bar label/hint above. "In your cart" only when the whole routine is owned;
    // never while add-ons remain (that was the desync bug).
    const btn = this.querySelector('[data-xs-add]');
    if (btn) {
      let label, disabled;
      if (this.configurationError) {
        label = this.t('configuration_error', 'Unavailable');
        disabled = true;
      } else if (!heroInCart && (this.heroAvailable !== true || !heroPurchase.valid)) {
        label = this.t('btn_sold_out', 'Sold out');
        disabled = true;
      } else if (everythingInCart) {
        label = this.t('btn_in_cart', 'In your cart');
        disabled = true;
      } else if (heroInCart) {
        if (toAdd.length === 0) { label = this.t('btn_pick_addon', 'Pick an add-on'); disabled = true; }
        else { label = this.t('btn_add_to_order', 'Add ' + toAdd.length + ' to your order', { count: toAdd.length }); disabled = false; }
      } else {
        label = toAdd.length === 0 ? this.t('btn_add_to_cart', 'Add to cart') : this.t('btn_add_count', 'Add ' + addCount + ' items to cart', { count: addCount });
        disabled = false;
      }
      if (this.addFailureVisible && !disabled) label = this.t('try_again', 'Try again');
      setTranslatedText(btn, label);
      btn.disabled = this.designMode
        || disabled
        || this.addInFlight
        || this.addOutcomeUnknown
        || this.variantSelectionBlocked
        || this.configurationError;
    }

    this.setText('[data-xs-freeship]', this.t('ships_free', 'Your order ships free.'));
  }

  setTileState(tile, txt) { setTranslatedText(tile.querySelector('[data-xs-state]'), txt); }

  variantId(value) {
    const raw = String(value == null ? '' : value).trim();
    if (!/^\d+$/.test(raw)) return null;
    const id = Number(raw);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  }

  async add() {
    if (this.designMode || !this.cartReady || this.addInFlight || this.addOutcomeUnknown || this.pendingAdd || this.variantSelectionBlocked) return;
    if (!this.cartAddUrl) {
      this.configurationError = true;
      this.render();
      this.announce('configuration_error', 'Unavailable');
      return;
    }

    const heroPurchase = this.heroPurchase();
    const heroAdd = !this.heroInCart(heroPurchase);
    if (heroAdd && (this.heroAvailable !== true || !heroPurchase.valid)) {
      this.configurationError = true;
      this.render();
      this.announce('configuration_error', 'Unavailable');
      return;
    }
    const intendedVariantIds = [];
    if (heroAdd) intendedVariantIds.push(this.heroVariantId);
    this.tiles.forEach((t) => {
      if (!this.owned(t) && this.visible(t) && this.picked.has(t.dataset.variantId)) intendedVariantIds.push(t.dataset.variantId);
    });
    if (!intendedVariantIds.length) return;

    // Never silently drop a malformed or duplicate ID and submit a partial kit.
    const parsedVariantIds = intendedVariantIds.map((id) => this.variantId(id));
    const uniqueVariantIds = new Set(parsedVariantIds);
    if (parsedVariantIds.some((id) => id == null) || uniqueVariantIds.size !== intendedVariantIds.length) {
      this.configurationError = true;
      this.render();
      this.announce('configuration_error', 'Unavailable');
      return;
    }
    const items = parsedVariantIds.map((id, index) => {
      const item = { id, quantity: 1 };
      if (heroAdd && index === 0 && heroPurchase.sellingPlanId) {
        item.selling_plan = Number(heroPurchase.sellingPlanId);
      }
      return item;
    });
    const sellingPlanIds = items.map((item) => item.selling_plan ? String(item.selling_plan) : null);
    const sectionIds = [...new Set(
      Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => String(component.dataset.sectionId || '').trim())
        .filter(Boolean)
    )].slice(0, 5);
    const requestBody = { items };
    if (sectionIds.length) {
      requestBody.sections = sectionIds;
      requestBody.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    const btn = this.querySelector('[data-xs-add]');
    const transaction = {
      transactionId: this.newTransactionId(),
      productId: String(this.heroProductId),
      cartToken: this.cartToken(),
      variantIds: parsedVariantIds.map(String),
      sellingPlanIds,
      postStatus: 'posting',
      createdAt: Date.now(),
      resolved: false,
      resolution: '',
      resolvedAt: 0,
    };
    this.pendingResponseSections = null;
    this.pendingAdd = transaction;
    this.addOutcomeUnknown = true;
    if (!this.claimPendingAdd(transaction)) {
      this.addInFlight = false;
      this.lockPendingAdd(true);
      return;
    }
    this.addFailureVisible = false;
    this.addInFlight = true;
    this.tiles.forEach((tile) => { tile.disabled = true; });
    if (btn) { btn.disabled = true; setTranslatedText(btn, this.t('adding', 'Adding…')); }

    this.cartUpdate = beginPogiCartUpdate({
      target: this,
      action: 'add',
      context: 'product',
      lines: parsedVariantIds.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
      source: this.sourceId || 'pogi-cross-sell',
      detail: {
        transactionId: transaction.transactionId,
        sellingPlanIds,
      },
    });

    let mutationResult = null;
    let mutationError = null;
    try {
      mutationResult = await addPogiCartLines({
        cartAddUrl: this.cartAddUrl,
        cartJsonUrl: this.cartUrl,
        items,
        requestBody: JSON.stringify(requestBody),
        requestOptions: {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        },
        timeoutMs: 12000,
      });
      transaction.postStatus = 'success';
      const rendered = mutationResult.addPayload?.sections;
      if (rendered && typeof rendered === 'object' && !Array.isArray(rendered)) {
        const sections = {};
        sectionIds.forEach((sectionId) => {
          if (typeof rendered[sectionId] === 'string' && rendered[sectionId]) {
            sections[sectionId] = rendered[sectionId];
          }
        });
        if (Object.keys(sections).length && this.pendingAdd === transaction) {
          this.pendingResponseSections = {
            transactionId: transaction.transactionId,
            sections,
          };
        }
      }
      this.persistPendingAdd();
    } catch (e) {
      mutationError = e;
      transaction.postStatus = e?.retrySafe === true ? 'failed' : 'unknown';
      this.persistPendingAdd();
    }

    // A same-element disconnect/reconnect may supersede this transaction only
    // by retaining and reconciling the same pending object; never erase intent.
    if (this.pendingAdd !== transaction) {
      this.addInFlight = false;
      return;
    }

    this.addInFlight = false;

    if (mutationResult) {
      this.applyCart(mutationResult.cart);
      const responseSections = this.takeResponseSections(transaction.transactionId);
      const shouldNotify = this.resolveStoredPending(transaction, 'confirmed');
      this.pendingAdd = null;
      this.addOutcomeUnknown = false;
      this.addFailureVisible = false;
      this.setSyncState('ready');
      this.render();
      if (shouldNotify) {
        if (!this.notifyCartSuccess(responseSections)) this.focusSectionHeading();
      } else {
        this.cartUpdate?.resolve(mutationResult.cart, {
          recovered: mutationResult.recovered,
          commitState: mutationResult.commitState,
        });
        this.cartUpdate = null;
      }
      return;
    }

    const currentCart = mutationError?.currentCart || null;
    if (currentCart) this.applyCart(currentCart);
    const definiteFailure = mutationError?.retrySafe === true;
    const resolution = definiteFailure ? 'failed' : 'uncertain';
    this.addFailureVisible = true;
    this.cartUpdate?.fail(mutationError, currentCart, {
      commitState: mutationError?.commitState,
      retrySafe: mutationError?.retrySafe,
      resolution,
    });
    this.cartUpdate = null;

    if (definiteFailure) {
      this.resolveStoredPending(transaction, resolution);
      this.pendingAdd = null;
      this.addOutcomeUnknown = false;
      this.setSyncState('ready');
      this.render();
      this.announce('add_error', 'Error adding to cart');
      this.focusReadyAction();
      return;
    }

    // A transport-uncertain mutation remains an unresolved, product-scoped
    // transaction. Keeping the exact intent in memory/session storage prevents
    // this instance, a sibling instance, or a reconnect from exposing a second
    // POST while Shopify may still commit the first one.
    this.pendingAdd = transaction;
    this.addOutcomeUnknown = true;
    this.dataset.cartStatusUncertain = 'true';
    this.setSyncState('ready');
    this.render();
    this.setSyncState('uncertain');
    this.announce('cart_uncertain', 'Cart status changed. Opening your cart…');
    const fullCartUrl = this.cartUrl.replace(/\.js(?:\?.*)?$/, '');
    window.setTimeout(() => window.location.assign(fullCartUrl), 2500);
  }
}

if (!customElements.get('pogi-cross-sell')) {
  customElements.define('pogi-cross-sell', PogiCrossSell);
}
